import React, { Component } from 'react';
import styleable from 'react-styleable';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { DataTable } from '@3plearning/react-components';
import cloneDeep from 'lodash/cloneDeep';

import css from './single-class.scss';

import exportReportActions from '../../../components/sub-heading/export-report/export-report-actions';
import classActions, {
    SKILL_QUESTS_MASTERY_CLASS_RETRIEVED
} from './single-class-actions';
import activeFiltersActions from '../../../components/report-filters/redux/active-filters/active-filters-actions';

import {
    ASSIGNED_QUESTS,
    COMPLETED_QUESTS,
    COMPLETED_QUESTS_SUMMARY,
    STUDENT_FULL_NAME,
    SKILL_QUESTS_MASTERY_LEGENDS_VARIANTS,
    GROUP_NAME
} from '../skill-quests-mastery-constants';
import singleClassTableConfig from './single-class-constants';
import {
    SELECTED_CLASS,
    SELECTED_DATE,
    SELECTED_GROUP,
    SELECTED_STUDENT,
    ALL_GROUPS
} from '../../../components/report-filters/filter-constants';
import { activeFiltersPropType } from '../../../components/report-filters/filter-prop-types';

import {
    alignTableConfigHeader,
    alignSingleClassTableConfigBody,
    alignTableConfigNested,
    updateTableConfigTransformById,
    renderCompletedQuestsGroupLevel,
    manageHeaderValue,
    onInvalidRequest,
    manageEmptyGroup,
    manageTranslation,
    translateTableContent
} from '../skill-quests-mastery-helpers';
import FilterHelper from '../../../helpers/filter-helper';

import activeFiltersSelectors from '../../../components/report-filters/redux/active-filters/active-filters-selectors';

import SubHeadingButtons from '../../../components/sub-heading/sub-heading-buttons/sub-heading-buttons';
import SkillQuestsMasteryLegends from '../../../components/skill-quests-mastery-legends/skill-quests-mastery-legends';
import AssignmentResult from '../../../components/assignment-result/assignment-result';

export class SingleClass extends Component {
    static propTypes = {
        // redux filter values
        activeFilters: activeFiltersPropType,
        // Download report handler
        downloadStudentReport: PropTypes.func,
        // Export report handler
        exportReport: PropTypes.func,
        // Init data service request
        getClassSkillQuestsMasteryData: PropTypes.func,
        // Is requesting download report
        isProcessingDownload: PropTypes.bool,
        // Manages active filter redux values
        updateActiveFilter: PropTypes.func,
        // Active filters selected class id
        selectedClassId: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number
        ]),
        // BE Data for skill quests mastery classes
        skillQuestsMasterySingleClass: PropTypes.object,
        // Assignment result filter
        assignmentResultType: PropTypes.number
    };

    constructor(props) {
        super(props);

        this.state = {
            tableConfig: singleClassTableConfig
        };
    }

    componentDidMount() {
        const { activeFilters, assignmentResultType } = this.props;
        if (FilterHelper.shouldLoadSingleClass(activeFilters)) {
            this.props.getClassSkillQuestsMasteryData(
                activeFilters[SELECTED_CLASS].id,
                activeFilters[SELECTED_DATE],
                assignmentResultType
            );
        }
    }

    componentWillReceiveProps(newProps) {
        this.onParamChange(this.props, newProps);
        this.onDataChange(this.props, newProps);
        onInvalidRequest(
            this.props,
            newProps,
            SKILL_QUESTS_MASTERY_CLASS_RETRIEVED
        );
    }

    /**
     * ON PROPS CHANGE HANDLERS
     */
    onParamChange = (props, newProps) => {
        const { activeFilters, assignmentResultType } = props;
        const {
            activeFilters: newActiveFilters,
            assignmentResultType: newAssignmentResultType
        } = newProps;

        const shouldReload =
            FilterHelper.shouldReloadSingleClass(
                activeFilters,
                newActiveFilters
            ) || assignmentResultType !== newAssignmentResultType;

        if (shouldReload) {
            this.props.getClassSkillQuestsMasteryData(
                newActiveFilters[SELECTED_CLASS].id,
                newActiveFilters[SELECTED_DATE],
                newAssignmentResultType
            );
        }
    };

    onDataChange = (props, newProps) => {
        const { skillQuestsMasterySingleClass } = props;
        const {
            skillQuestsMasterySingleClass: newSkillQuestsMasterySingleClass
        } = newProps;

        if (
            skillQuestsMasterySingleClass !== newSkillQuestsMasterySingleClass
        ) {
            this.setTableConfig(newProps);
        }
    };

    /**
     * SETUP TABLE CONFIG
     */
    navigateToStudentLevel = (filterName, selectedItem) => {
        this.props.updateActiveFilter(filterName, {
            id: selectedItem.id,
            name: selectedItem.name
        });
    };

    tableConfigTranslations = (updatedTableConfig) => {
        const i18n = window.i18n;

        updatedTableConfig.cols.find(
            (col) => col.id === GROUP_NAME.id
        ).name = manageTranslation(GROUP_NAME, i18n);
        translateTableContent(i18n, updatedTableConfig);

        return updatedTableConfig;
    };

    updateTableConfigBody = (updatedTableConfig) => {
        const selectedClassId = this.props.selectedClassId;
        const i18n = window.i18n;

        updatedTableConfig = alignSingleClassTableConfigBody(
            updatedTableConfig
        );
        updatedTableConfig = updateTableConfigTransformById(
            COMPLETED_QUESTS_SUMMARY.id,
            (row) =>
                renderCompletedQuestsGroupLevel(row, selectedClassId, i18n),
            updatedTableConfig
        );
        updatedTableConfig = updateTableConfigTransformById(
            ASSIGNED_QUESTS.id,
            (row) =>
                manageEmptyGroup(
                    row,
                    manageHeaderValue(row[ASSIGNED_QUESTS.id])
                ),
            updatedTableConfig
        );
        updatedTableConfig = updateTableConfigTransformById(
            COMPLETED_QUESTS.id,
            (row) =>
                manageEmptyGroup(
                    row,
                    manageHeaderValue(row[COMPLETED_QUESTS.id])
                ),
            updatedTableConfig
        );
        updatedTableConfig = this.tableConfigTranslations(updatedTableConfig);

        return updatedTableConfig;
    };

    updateTableConfigNested = (updatedTableConfig) => {
        updatedTableConfig = alignTableConfigNested(updatedTableConfig);

        updatedTableConfig.nestedData.cols.find(
            (col) => col.id === STUDENT_FULL_NAME.id
        ).onClick = (selectedStudent) =>
            this.navigateToStudentLevel(SELECTED_STUDENT, selectedStudent);
        return updatedTableConfig;
    };

    setTableConfig = (props) => {
        const { skillQuestsMasterySingleClass } = props;

        let updatedTableConfig = cloneDeep(singleClassTableConfig);

        if (skillQuestsMasterySingleClass) {
            updatedTableConfig = alignTableConfigHeader(
                skillQuestsMasterySingleClass,
                updatedTableConfig
            );
            updatedTableConfig = this.updateTableConfigBody(updatedTableConfig);
            updatedTableConfig = this.updateTableConfigNested(
                updatedTableConfig
            );
        }

        this.setState({
            tableConfig: updatedTableConfig
        });
    };

    getTableData = () => {
        const { activeFilters, skillQuestsMasterySingleClass } = this.props;
        const selectedGroupId = activeFilters[SELECTED_GROUP].id;

        if (selectedGroupId !== ALL_GROUPS) {
            return skillQuestsMasterySingleClass.groups.filter(
                (group) => group.id === selectedGroupId
            );
        }

        return skillQuestsMasterySingleClass.groups;
    };

    render() {
        const {
            downloadStudentReport,
            exportReport,
            isProcessingDownload,
            skillQuestsMasterySingleClass
        } = this.props;
        const { tableConfig } = this.state;

        return (
            <div className={css['sqm-single-class']}>
                <div className={css['sub-heading']}>
                    <div className={css['right-side']}>
                        <SkillQuestsMasteryLegends
                            variant={
                                SKILL_QUESTS_MASTERY_LEGENDS_VARIANTS.CLASS_GROUP_LEVEL
                            }
                        />
                        <AssignmentResult />
                        <SubHeadingButtons
                            isDownloadEnabled
                            isExportEnabled
                            isProcessingDownload={isProcessingDownload}
                            onExportReport={exportReport}
                            onDownloadReport={downloadStudentReport}
                        />
                    </div>
                </div>
                <div className={css['data-table']}>
                    {skillQuestsMasterySingleClass && (
                        <DataTable
                            config={tableConfig}
                            data={this.getTableData()}
                        />
                    )}
                </div>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        activeFilters: activeFiltersSelectors.selectActiveFilters(state),
        selectedClassId: activeFiltersSelectors.selectClassId(state),
        invalidRequest: state.invalidRequest,
        isProcessingDownload: state.downloadReport.isProcessingDownload,
        skillQuestsMasterySingleClass: state.skillQuestsMasterySingleClass.data,
        assignmentResultType: state.assignmentResultType
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            exportReport: exportReportActions.exportReport,
            downloadStudentReport: exportReportActions.downloadStudentReport,
            getClassSkillQuestsMasteryData:
                classActions.getClassSkillQuestsMasteryData,
            updateActiveFilter: activeFiltersActions.updateActiveFilter
        },
        dispatch
    );
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(styleable(css)(SingleClass));



// WEBPACK FOOTER //
// ./containers/skill-quests-mastery/single-class/single-class.js